import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Scissors, Clock, User, Calendar as CalIcon, Check, ChevronRight,
  ChevronLeft, Loader2, CheckCircle2, Info, Mail, Phone, Eye, EyeOff, Lock, LogIn,
} from "lucide-react";

const STEPS = ["Service", "Date & Time", "Your Details", "Confirm"];

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseYmd = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const ddmm = (s) => { if (!s) return ""; const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };

const groupSlots = (slots) => {
  const g = { Morning: [], Afternoon: [], Evening: [] };
  (slots || []).forEach((s) => {
    const h = parseInt(s.split(":")[0], 10);
    if (h < 12) g.Morning.push(s);
    else if (h < 17) g.Afternoon.push(s);
    else g.Evening.push(s);
  });
  return g;
};

const dpClassNames = {
  months: "w-full",
  month: "w-full space-y-2",
  caption: "flex justify-center pt-1 relative items-center mb-2",
  caption_label: "text-base font-display text-white tracking-wide",
  nav: "flex items-center",
  nav_button:
    "h-8 w-8 rounded-full border border-white/15 text-gray-300 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-colors duration-200",
  nav_button_previous: "absolute left-1",
  nav_button_next: "absolute right-1",
  table: "w-full border-collapse",
  head_row: "flex justify-between",
  head_cell: "text-gray-500 font-normal text-xs w-10 sm:w-12 text-center",
  row: "flex w-full justify-between mt-1.5",
  cell: "relative p-0 text-center",
  day: "h-10 w-10 sm:h-12 sm:w-12 p-0 font-normal rounded-xl text-gray-200 hover:bg-white/10 transition-colors duration-150 aria-selected:opacity-100",
  day_selected: "!bg-[#D4AF37] !text-black hover:!bg-[#D4AF37] font-semibold",
  day_today: "border border-[#D4AF37]/60 text-[#D4AF37]",
  day_disabled: "!text-gray-700 opacity-40 hover:bg-transparent cursor-not-allowed",
  day_hidden: "invisible",
};

export default function Book() {
  const navigate = useNavigate();
  const { user, login, register, logout } = useAuth();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [barber, setBarber] = useState(null);
  const [selected, setSelected] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [monthData, setMonthData] = useState(null);
  const [date, setDate] = useState(null); // "YYYY-MM-DD"
  const [slots, setSlots] = useState(null);
  const [slotInfo, setSlotInfo] = useState({});
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "", password: "", confirm: "" });
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [remember, setRemember] = useState(true);
  const [mode, setMode] = useState("new"); // logged-out only: "new" | "returning"
  const [authBusy, setAuthBusy] = useState(false);

  useEffect(() => { if (user) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "", phone: user.phone || "" })); }, [user]);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      const pre = new URLSearchParams(window.location.search).get("services");
      if (pre) {
        const ids = pre.split(",");
        const match = r.data.filter((s) => ids.includes(s.id));
        if (match.length) setSelected(match);
      }
    }).catch(() => {});
    api.get("/barbers").then((r) => setBarber(r.data[0] || null)).catch(() => {});
  }, []);

  const svcParam = selected.map((s) => s.id).join(",");
  const totalPrice = selected.reduce((t, s) => t + s.price, 0);
  const totalDuration = selected.reduce((t, s) => t + s.duration, 0);
  const toggleSvc = (s) => { setSelected((cur) => cur.find((x) => x.id === s.id) ? cur.filter((x) => x.id !== s.id) : [...cur, s]); setDate(null); setTime(null); setMonthData(null); };

  const fetchMonth = useCallback((m) => {
    if (!barber || !svcParam) return;
    setMonthData(null);
    api.get("/availability/month", { params: { barber_id: barber.id, service_id: svcParam, year: m.getFullYear(), month: m.getMonth() + 1 } })
      .then((r) => setMonthData(r.data.days))
      .catch(() => setMonthData({}));
  }, [barber, svcParam]);

  useEffect(() => {
    if (step === 1 && barber && svcParam) fetchMonth(month);
  }, [step, barber, svcParam, month, fetchMonth]);

  useEffect(() => {
    if (step === 1 && barber && svcParam && date) {
      setSlots(null); setTime(null);
      api.get("/availability", { params: { barber_id: barber.id, service_id: svcParam, date } })
        .then((r) => { setSlots(r.data.slots); setSlotInfo(r.data); })
        .catch(() => setSlots([]));
    }
  }, [step, barber, svcParam, date]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const phoneValid = form.phone.replace(/[^\d]/g, "").length >= 7;
  const pwValid = form.password.length >= 6;
  const pwMatch = form.password === form.confirm;

  const checkEmail = async () => {
    if (user || !emailValid) return;
    try {
      const { data } = await api.get("/auth/check-email", { params: { email: form.email } });
      setMode(data.exists ? "returning" : "new");
    } catch { setMode("new"); }
  };

  const doSignIn = async () => {
    setAuthBusy(true);
    try {
      const u = await login(form.email, form.password, remember);
      if (u.role !== "customer") {
        await logout();
        toast.error("This email belongs to a staff account. Please use the admin login.");
        return;
      }
      toast.success("Signed in — continuing your booking");
      setForm((f) => ({ ...f, name: u.name || f.name, phone: u.phone || f.phone, password: "", confirm: "" }));
    } catch (err) { toast.error(apiError(err)); }
    finally { setAuthBusy(false); }
  };

  const detailsValid = user
    ? (form.name.trim().length >= 2 && emailValid && phoneValid)
    : (mode === "new" && form.name.trim().length >= 2 && emailValid && phoneValid && pwValid && pwMatch);

  const confirm = async () => {
    setBooking(true);
    try {
      if (!user) {
        await register({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), password: form.password, remember });
      }
      const { data } = await api.post("/appointments", {
        service_ids: selected.map((s) => s.id), barber_id: barber.id, date, start_time: time,
        notes: form.notes.trim(), customer_phone: form.phone.trim(),
      });
      setDone(data);
    } catch (err) {
      const msg = apiError(err);
      if (/already registered/i.test(msg)) { setMode("returning"); setStep(2); toast.error("You already have an account — please sign in to continue."); }
      else toast.error(msg);
    } finally {
      setBooking(false);
    }
  };

  const canNext =
    (step === 0 && selected.length > 0) || (step === 1 && date && time) || (step === 2 && detailsValid);

  // Calendar modifier/disabled arrays
  const days = monthData || {};
  const disabledDays = Object.entries(days).filter(([, st]) => st !== "available").map(([s]) => parseYmd(s));
  const fullDays = Object.entries(days).filter(([, st]) => st === "full").map(([s]) => parseYmd(s));
  const today0 = new Date(); today0.setHours(0, 0, 0, 0);
  const maxDate = new Date(today0.getFullYear(), today0.getMonth() + 3, today0.getDate());

  if (done) {
    return (
      <div data-testid="booking-success" className="pt-32 pb-24 max-w-lg mx-auto px-5 text-center relative z-10">
        <CheckCircle2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-display text-5xl tracking-tight mb-3">Request received!</h1>
          <p className="text-gray-400 mb-8">Your booking is awaiting confirmation from the shop. You can track its status any time under "My Bookings".</p>
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-8 text-left space-y-3">
            <Row label="Reference" value={done.ref} />
            <Row label="Service" value={done.service_name} />
            <Row label="Date" value={ddmm(done.date)} />
            <Row label="Time" value={`${done.start_time} – ${done.end_time}`} />
            <Row label="Price" value={`£${done.price}`} />
            <Row label="Name" value={done.customer_name} />
          </div>
          <div className="flex gap-3 mt-8 justify-center">
            <button data-testid="book-another" onClick={() => { setDone(null); setStep(0); setSelected([]); setDate(null); setTime(null); setSlots(null); setForm((f) => ({ ...f, notes: "" })); }} className="px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300">Book Another</button>
            <button onClick={() => navigate("/")} className="px-6 py-3 rounded-full border border-white/20 hover:border-white/50 transition-colors duration-300">Back to Home</button>
          </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="pt-32 pb-24 max-w-4xl mx-auto px-5 sm:px-8 relative z-10">
        <div className="label-caps mb-3">Reserve your chair</div>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tighter mb-10">Book Appointment</h1>

        {/* stepper */}
        <div className="flex items-center mb-12">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-2">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm border transition-colors duration-300 ${
                  i < step ? "bg-[#D4AF37] border-[#D4AF37] text-black" :
                  i === step ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/20 text-gray-500"}`}>
                  {i < step ? <Check className="w-4 h-4" /> : i + 1}
                </span>
                <span className={`text-sm hidden sm:inline ${i <= step ? "text-white" : "text-gray-500"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-3 ${i < step ? "bg-[#D4AF37]" : "bg-white/10"}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* STEP 0 SERVICE */}
        {step === 0 && (
          <div>
            <p className="text-sm text-gray-400 mb-4">Select one or more services — durations and prices are combined into a single appointment.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((s) => {
                const on = selected.find((x) => x.id === s.id);
                return (
                <button
                  key={s.id}
                  data-testid={`book-service-${s.id}`}
                  onClick={() => toggleSvc(s)}
                  className={`text-left p-6 rounded-2xl border transition-all duration-300 ${
                    on ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/5 bg-[#141414] hover:border-white/20"}`}
                >
                  <div className="flex justify-between items-start">
                    <span className={`w-6 h-6 rounded-md border flex items-center justify-center ${on ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/20"}`}>{on && <Check className="w-4 h-4 text-black" />}</span>
                    <span className="font-display text-2xl text-[#D4AF37]">£{s.price}</span>
                  </div>
                  <h3 className="text-lg mt-4">{s.name}</h3>
                  <p className="text-gray-400 text-sm mt-1">{s.description}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-3"><Clock className="w-3 h-3" /> {s.duration} min</div>
                </button>
              );})}
            </div>
            {selected.length > 0 && (
              <div data-testid="book-service-total" className="mt-5 flex justify-between items-center bg-[#141414] border border-[#D4AF37]/30 rounded-xl px-5 py-3 text-sm">
                <span>{selected.length} service{selected.length > 1 ? "s" : ""} · {totalDuration} min total</span>
                <span className="font-display text-xl text-[#D4AF37]">£{totalPrice}</span>
              </div>
            )}
          </div>
        )}

        {/* STEP 1 DATE + TIME */}
        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calendar */}
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-5">
              <div data-testid="book-calendar" className="flex justify-center">
                {monthData === null ? (
                  <div className="flex justify-center items-center h-72"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
                ) : (
                  <DayPicker
                    mode="single"
                    weekStartsOn={1}
                    month={month}
                    onMonthChange={setMonth}
                    selected={date ? parseYmd(date) : undefined}
                    onSelect={(d) => { if (d) setDate(ymd(d)); }}
                    fromDate={today0}
                    toDate={maxDate}
                    disabled={[...disabledDays, { before: today0 }, { after: maxDate }]}
                    modifiers={{ fullday: fullDays }}
                    modifiersClassNames={{ fullday: "text-red-400/80 line-through" }}
                    classNames={dpClassNames}
                  />
                )}
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-white/10 text-xs text-gray-400">
                <LegendDot cls="border border-white/25" label="Available" />
                <LegendDot cls="bg-[#D4AF37]" label="Selected" />
                <LegendDot cls="bg-gray-700" label="Unavailable" />
                <LegendDot cls="bg-red-500/60" label="Fully booked" />
              </div>
            </div>

            {/* Time slots */}
            <div>
              {!date ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-gray-500 text-center">
                  <CalIcon className="w-10 h-10 mb-3 opacity-40" />
                  Select a date to see available times.
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <CalIcon className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-white">{parseYmd(date).toLocaleDateString("en-GB", { weekday: "long" })}, {ddmm(date)}</span>
                  </div>
                  {slotInfo.appointment_only && (
                    <div className="flex items-center gap-2 text-sm text-[#D4AF37] mb-4"><Info className="w-4 h-4" /> This day is appointment-only.</div>
                  )}
                  {slots === null ? (
                    <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>
                  ) : slots.length === 0 ? (
                    <div data-testid="no-slots" className="text-center py-10 text-gray-400 bg-[#141414] border border-white/10 rounded-2xl px-5">
                      No appointment times are available on this date. Please choose another date.
                    </div>
                  ) : (
                    <div className="max-h-[26rem] overflow-y-auto pr-1 space-y-5">
                      {Object.entries(groupSlots(slots)).map(([label, list]) => (
                        list.length > 0 && (
                          <div key={label}>
                            <div className="label-caps mb-2">{label}</div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                              {list.map((s) => (
                                <button
                                  key={s}
                                  data-testid={`book-slot-${s}`}
                                  onClick={() => setTime(s)}
                                  className={`py-3 rounded-xl border text-sm transition-all duration-200 ${
                                    time === s ? "border-[#D4AF37] bg-[#D4AF37] text-black font-medium" : "border-white/10 text-gray-300 hover:border-[#D4AF37]/50"}`}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 2 DETAILS */}
        {step === 2 && (
          <div className="max-w-xl space-y-5" data-testid="book-details">
            {user ? (
              <>
                <Field label="Full name" icon={<User className="w-4 h-4" />}>
                  <input data-testid="book-name" value={form.name} readOnly className={inputCls + " opacity-70 cursor-not-allowed"} />
                </Field>
                <Field label="Email address" icon={<Mail className="w-4 h-4" />}>
                  <input data-testid="book-email" value={form.email} readOnly className={inputCls + " opacity-70 cursor-not-allowed"} />
                </Field>
                <Field label="Mobile number" icon={<Phone className="w-4 h-4" />}>
                  <input data-testid="book-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07XXX XXXXXX" className={inputCls} />
                </Field>
                <Field label="Notes (optional)">
                  <textarea data-testid="book-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any requests for your barber?" className={inputCls} />
                </Field>
                <p className="text-xs text-gray-500">Booking under your account. Payment is taken in-shop. Track status any time in "My Bookings".</p>
              </>
            ) : (
              <>
                <Field label="Email address" icon={<Mail className="w-4 h-4" />}>
                  <input data-testid="book-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} onBlur={checkEmail} placeholder="you@email.com" className={inputCls} />
                </Field>

                {mode === "returning" ? (
                  <div className="space-y-4 bg-[#141414] border border-[#D4AF37]/30 rounded-2xl p-5" data-testid="book-returning">
                    <p className="text-sm text-[#D4AF37] flex items-center gap-2"><LogIn className="w-4 h-4" /> Already booked with us? Sign in to continue — we'll keep your service, date and time.</p>
                    <Field label="Password" icon={<Lock className="w-4 h-4" />}>
                      <div className="relative">
                        <input data-testid="book-signin-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Your password" className={inputCls + " pr-12"} />
                        <button type="button" data-testid="book-signin-password-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className={eyeBtn}>{showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                      </div>
                    </Field>
                    <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer"><input type="checkbox" data-testid="book-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#D4AF37]" /> Remember me</label>
                    <div className="flex items-center justify-between gap-3">
                      <button data-testid="book-signin" onClick={doSignIn} disabled={authBusy || !form.password} className="px-6 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-50 flex items-center gap-2">{authBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}</button>
                      <a href="/forgot-password" className="text-sm text-gray-400 hover:text-[#D4AF37]">Forgot password?</a>
                    </div>
                    <button data-testid="book-use-different-email" onClick={() => { setMode("new"); setForm({ ...form, email: "", password: "" }); }} className="text-xs text-gray-500 hover:text-gray-300">Use a different email</button>
                  </div>
                ) : (
                  <>
                    <Field label="Full name" icon={<User className="w-4 h-4" />}>
                      <input data-testid="book-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" className={inputCls} />
                    </Field>
                    <Field label="Mobile number" icon={<Phone className="w-4 h-4" />}>
                      <input data-testid="book-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="07XXX XXXXXX" className={inputCls} />
                    </Field>
                    <Field label="Create password" icon={<Lock className="w-4 h-4" />}>
                      <div className="relative">
                        <input data-testid="book-password" type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" className={inputCls + " pr-12"} />
                        <button type="button" data-testid="book-password-toggle" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className={eyeBtn}>{showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                      </div>
                    </Field>
                    <Field label="Confirm password" icon={<Lock className="w-4 h-4" />}>
                      <div className="relative">
                        <input data-testid="book-confirm-password" type={showConfirm ? "text" : "password"} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} placeholder="Re-enter password" className={inputCls + " pr-12"} />
                        <button type="button" data-testid="book-confirm-password-toggle" onClick={() => setShowConfirm((v) => !v)} aria-label={showConfirm ? "Hide password" : "Show password"} className={eyeBtn}>{showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
                      </div>
                    </Field>
                    {form.confirm.length > 0 && !pwMatch && <p data-testid="book-pw-mismatch" className="text-xs text-red-400">Passwords do not match.</p>}
                    <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer"><input type="checkbox" data-testid="book-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#D4AF37]" /> Remember me</label>
                    <Field label="Notes (optional)">
                      <textarea data-testid="book-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any requests for your barber?" className={inputCls} />
                    </Field>
                    <p className="text-xs text-gray-500">We'll create your account so you can track and manage this booking. No online payment — you pay in-shop.</p>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* STEP 3 CONFIRM */}
        {step === 3 && (
          <div className="bg-[#141414] border border-white/10 rounded-2xl p-8 space-y-4" data-testid="book-summary">
            <Row label="Services" value={selected.map((s) => s.name).join(", ")} icon={<Scissors className="w-4 h-4" />} />
            <Row label="Total price" value={`£${totalPrice}`} />
            <Row label="Duration" value={`${totalDuration} min`} icon={<Clock className="w-4 h-4" />} />
            <Row label="Date" value={`${parseYmd(date).toLocaleDateString("en-GB", { weekday: "long" })}, ${ddmm(date)}`} icon={<CalIcon className="w-4 h-4" />} />
            <Row label="Time" value={time} icon={<Clock className="w-4 h-4" />} />
            <Row label="Name" value={form.name} icon={<User className="w-4 h-4" />} />
            <Row label="Phone" value={form.phone} icon={<Phone className="w-4 h-4" />} />
            <Row label="Email" value={form.email} icon={<Mail className="w-4 h-4" />} />
            {form.notes && <Row label="Notes" value={form.notes} />}
          </div>
        )}

        {/* NAV BUTTONS */}
        <div className="flex justify-between mt-10">
          <button
            data-testid="book-back"
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="px-6 py-3 rounded-full border border-white/20 text-gray-300 hover:border-white/50 transition-colors duration-300 disabled:opacity-30 flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < 3 ? (
            <button
              data-testid="book-next"
              onClick={() => setStep(step + 1)}
              disabled={!canNext}
              className="px-8 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-40 flex items-center gap-2"
            >
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="book-confirm"
              onClick={confirm}
              disabled={booking}
              className="px-8 py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3E5AB] transition-colors duration-300 disabled:opacity-60 flex items-center gap-2"
            >
              {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Booking Request"}
            </button>
          )}
        </div>
      </div>
  );
}

const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#D4AF37] focus:ring-0 transition-colors duration-200";
const eyeBtn = "absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#D4AF37] transition-colors duration-200";
const Field = ({ label, icon, children }) => (
  <div>
    <label className="label-caps mb-2 flex items-center gap-2">{icon}{label}</label>
    {children}
  </div>
);
const LegendDot = ({ cls, label }) => (
  <span className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded-md ${cls}`} />{label}</span>
);
const Row = ({ label, value, icon }) => (
  <div className="flex justify-between items-center gap-4 border-b border-white/5 pb-3 last:border-0">
    <span className="text-gray-400 flex items-center gap-2 text-sm shrink-0">{icon}{label}</span>
    <span className="text-white text-right">{value}</span>
  </div>
);
